import { BaseState, CommonActionTypes, Metadata, CommonGraph } from '@common/types';
import { DidChangePayload, DidUpdatePayload } from '@common/types/messageTypes';
import { LSP } from '@LSP/base/types';
import { ArrayElement } from '@prophecy/interfaces/generic';
import { matchesPropertyPath } from '@prophecy/utils/data';
import { isNodeEvnProduction } from '@prophecy/utils/env';
import { ProphecyError } from '@prophecy/utils/error';
import produce from 'immer';
import { set } from 'lodash-es';

import {
  getCurrentProcessGraphPath,
  getGraphProcess,
  isComponentProperty,
  isGraphProperty,
  isMetadataProperty,
  updateComponentProperty,
  updateGraphProperty,
  updateMetadataProperty
} from '../../Parser/bindings';

export interface LoaderState {
  loader: boolean;
  loaderCount: number;
}

export function increaseLoaderCount(draft: LoaderState, hasCountSame = false): void {
  draft.loader = true;
  if (!hasCountSame) {
    draft.loaderCount += 1;
  }
}

export function decreaseLoaderCount(draft: LoaderState, resetLoaderCount?: boolean): void {
  draft.loaderCount -= 1;
  if (draft.loaderCount <= 0 || resetLoaderCount) {
    draft.loader = false;
    draft.loaderCount = 0;
  }
}

const mergePayload = (payload: DidUpdatePayload) => {
  const outputPayload: Map<string, ArrayElement<DidUpdatePayload>> = new Map();

  payload.forEach((nextChange) => {
    const { property } = nextChange;
    let foundMatch = false;

    outputPayload.forEach((prevChange) => {
      const { property: prevProperty } = prevChange;
      // if this property has already been modified replace its value with new one
      if (property === prevProperty) {
        outputPayload.set(property, nextChange);
        foundMatch = true;
        return;
      } else if (matchesPropertyPath(property, prevProperty)) {
        let mergedChange = nextChange;

        // if a parent has already been modified in same entry, merge the change to the parent change instead
        // as when doing undo we do in reverse order, if parent was newly added sending child property change without parent will cause an error
        // prevProperty will be the parentPath in property
        // prevProperty can be be array or object, so property path can be 'a.b.c' or a.b[2],
        // so if we remove a.b from path we need to remove preceding dot in case of oject property
        const childPath = property.replace(prevProperty, '').replace(/^\./, '');
        const value = produce(prevChange.value as object, (draft) => {
          set(draft, childPath, nextChange.value);
        });
        mergedChange = { ...prevChange, value };

        foundMatch = true;
        outputPayload.set(prevProperty, mergedChange);
      } else if (matchesPropertyPath(prevProperty, property)) {
        // if the parent path is set the child path would be reset, so remove them.
        outputPayload.delete(prevProperty);
      }
    });

    // if the property was not already tracked
    if (!foundMatch) {
      outputPayload.set(nextChange.property, nextChange);
    }
  });

  return Array.from(outputPayload.values());
};

function applyChanges<G extends CommonGraph>({
  graph,
  metadata,
  changes,
  rootPath,
  graphPath,
  componentId,
  updateIfParentExist
}: {
  graph: G;
  metadata: Metadata;
  changes: DidUpdatePayload;
  rootPath: string;
  graphPath?: string;
  componentId?: string;
  // as UI can be on future state, there can be cases where backend is trying to send didUpdate from past request, and the parent object is removed by UI
  // In such cases we want to ignore the changes, if coming from backend
  // if changes coming from UI, we don't want to have this limitation as UI always apply changes on latest
  updateIfParentExist?: boolean;
}) {
  let wipGraph = graph;
  let wipMetadata = metadata;

  // recursive set is causing immer to throw readonly property error
  // ex: set('a', { b }) -> set('a.b') is throwing error cannot set 'b' of readonly a
  // merging will first merge the second change into first before setting to immer, to avoid this issue
  mergePayload(changes).forEach((change) => {
    if (change.type === 'partial') {
      const { property, value } = change;
      if (!property) {
        if (isNodeEvnProduction()) {
          // silently skip update
          return;
        } else {
          // throw error in development environment
          throw new ProphecyError(`Property path can't be empty`);
        }
      }

      if (isComponentProperty(property)) {
        // component.properties.expression "abc"
        // allow only changes at process level
        const processGraphPath = getCurrentProcessGraphPath(componentId as string, graphPath as string);
        const process = getGraphProcess(wipGraph, processGraphPath, componentId as string, rootPath);
        updateComponentProperty(process, property, value, updateIfParentExist);
      } else if (isGraphProperty(property, rootPath)) {
        wipGraph = updateGraphProperty(wipGraph, property, value, rootPath, updateIfParentExist);
      } else if (isMetadataProperty(property)) {
        wipMetadata = updateMetadataProperty(wipMetadata, property, value, updateIfParentExist);
      }
    } else if (change.type === 'full') {
      const { value } = change;
      const { workflow, job, metadata } = value as {
        job: G;
        workflow: G;
        metadata: Metadata;
      };
      wipGraph = workflow || job;
      wipMetadata = metadata;
    }
  });

  return { graph: wipGraph, metadata: wipMetadata };
}
export function $updateState<T extends CommonGraph>(
  action: { type: CommonActionTypes.$updateState; payload: Partial<BaseState<T, Metadata>> },
  draft: BaseState<T, Metadata>
) {
  Object.entries(action.payload).forEach((item) => {
    const [key, value] = item as [keyof BaseState<T, Metadata>, never];
    draft[key] = value;
  });
}

export function propertiesDidReset<T extends CommonGraph>(draft: BaseState<T, Metadata>) {
  draft.currentGraph = draft.$graph;
}

export function propertiesDidSave<T extends CommonGraph>(draft: BaseState<T, Metadata>) {
  draft.$graph = draft.currentGraph;
}

export function propertiesDidUpdate<T extends CommonGraph>(
  action: {
    type: CommonActionTypes.propertyDidUpdate;
    savePending?: boolean | undefined;
    payload: DidUpdatePayload;
  },
  draft: BaseState<T, Metadata>
) {
  const { savePending, payload: changes } = action;

  const { metadata, graph: currentGraph } = applyChanges({
    graph: draft.currentGraph as T,
    metadata: draft.metadata as Metadata,
    changes,
    rootPath: draft.root,
    updateIfParentExist: true
  });
  draft.currentGraph = currentGraph;
  draft.metadata = metadata;

  /**
   * if any save is pending keep applying updates on main graph as well
   * otherwise if dirty state changes are discarded, we will loose updates triggered by a save
   */
  if (savePending) {
    const { graph: $graph } = applyChanges({
      graph: draft.$graph as T,
      metadata: draft.metadata as Metadata,
      changes,
      rootPath: draft.root,
      updateIfParentExist: true
    });
    draft.$graph = $graph;
  }
}

export function applyChangesToMasterAndDirtyGraph<T extends CommonGraph>(
  changes: DidUpdatePayload,
  draft: BaseState<T, Metadata>
) {
  // apply changes to dirty graph
  const { metadata, graph: currentGraph } = applyChanges({
    graph: draft.currentGraph as T,
    metadata: draft.metadata as Metadata,
    changes,
    rootPath: draft.root,
    updateIfParentExist: true
  });
  draft.currentGraph = currentGraph;
  draft.metadata = metadata;

  // apply changes to main graph
  const { graph: $graph } = applyChanges({
    graph: draft.$graph as T,
    metadata: draft.metadata as Metadata,
    changes,
    rootPath: draft.root,
    updateIfParentExist: true
  });
  draft.$graph = $graph;
}

export function propertiesDidChange<T extends CommonGraph>(
  action: {
    type: LSP.Method.propertiesDidChange;
    payload: DidChangePayload;
  },
  draft: BaseState<T, Metadata>
) {
  const payload = Array.isArray(action.payload) ? action.payload : [action.payload];
  const updatedPayload = payload.map((change) => ({ ...change, type: 'partial' })) as DidUpdatePayload;

  const { metadata, graph: currentGraph } = applyChanges({
    graph: draft.currentGraph as T,
    metadata: draft.metadata as Metadata,
    changes: updatedPayload,
    rootPath: draft.root,
    graphPath: draft.graphPath,
    componentId: draft.currentComponentId,
    // in case didChange is coming from UI, we don't want to strictly set the property
    // we want to allow creating nested object, as backend do not set default values, and can have binding to the nested path
    updateIfParentExist: false
  });
  draft.currentGraph = currentGraph;
  draft.metadata = metadata;
}
